import React, { useContext, useState } from "react";

import { Autorenew, Edit, MoreVert } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { getTimezone } from "countries-and-timezones";
import { Clipboard } from "react-feather";
import { Link } from "react-router-dom";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { User } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { toUUID } from "../../helpers/s12-helpers";
import DisplayHeading from "../shared/display-heading";
import Auth0LoginStatusButton from "./auth0-login-status-button";
import ConfirmUserModal from "./confirm-user-dialog";
import ConfirmUserSyncDialog from "./confirm-user-sync-dialog";
import PasswordResetEmailModal from "./password-reset-email-dialog";
import SetUserPasswordDialog from "./set-user-password-dialog";
import UpdateUserEmailModal from "./update-user-email-dialog";
import { UserAttributesDialog } from "./user-attributes/user-attributes-dialog";
import SettingsTable from "./user-settings-table";
import UserOrgsCountChip from "components/shared/chip/user-orgs-count-chip";
import TableLoading from "components/shared/table/table-loading";
import { languageDisplayMapping } from "constants/index";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { UserContext } from "context/user-context";
import {
  getSpeedMetrics,
  getTemperatureMetrics,
} from "helpers/metric-settings";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";
import { isEmaillessUser } from "helpers/user";
import { UserDetailsDataProps } from "types/user-display";

interface BasicTableProps {
  user: User | undefined;
  regionCode: string;
  isLoadingGetUserIdentity: boolean;
  identityID: string;
}

const BasicTable = ({
  user,
  regionCode,
  isLoadingGetUserIdentity,
  identityID,
}: BasicTableProps) => {
  const theme = useTheme();
  const createdAtDateString = user?.createdAt
    ? formatDateTimeDisplay(convertTimestampToDate(user?.createdAt))
    : "N/A";
  const userIdString = user?.id ?? "N/A";
  const userUUIDString = toUUID(userIdString);

  const tableCellSx = {
    borderColor: theme.palette.divider,
    borderStyle: "solid",
  };
  const leftCellSx = {
    ...tableCellSx,
    borderWidth: "0px 1px 1px 0px",
    fontWeight: "bold",
    width: "30%",
  };
  const leftBottomCellSx = {
    ...tableCellSx,
    borderWidth: "0px 1px 0px 0px",
    fontWeight: "bold",
  };
  const rightCellSx = {
    ...tableCellSx,
    borderWidth: "0px 0px 1px 0px",
    width: "70%",
  };
  const rightBottomCellSx = {
    ...tableCellSx,
    borderWidth: "0px",
  };

  const clickableSx = {
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  };
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const canSyncUserData =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.SYNC_USER_DATA]);
  const [userAttributeModalOpen, setUserAttributeModalOpen] = useState(false);
  const [resetEmailModalOpen, setResetEmailModalOpen] = useState(false);
  const [setPasswordModalOpen, setSetPasswordModalOpen] = useState(false);
  const [updateEmailModalOpen, setUpdateEmailModalOpen] = useState(false);
  const [confirmUserModalOpen, setConfirmUserModalOpen] = useState(false);
  const [confirmSyncModalOpen, setConfirmSyncModalOpen] = useState(false);

  const { copyToClipboard } = useContext(SnackbarContext);
  const shouldSpin = confirmSyncModalOpen ? "spin-1s" : "";

  return (
    <React.Fragment>
      {user?.id ? (
        <Box
          style={{
            borderColor: theme.palette.divider,
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "5px",
          }}
        >
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell
                  sx={{ ...leftCellSx, wordBreak: "normal" }}
                  align="right"
                >
                  {"ID"}
                </TableCell>
                <TableCell sx={rightCellSx} align="left">
                  <Typography
                    variant="body2"
                    sx={clickableSx}
                    data-anchor="user-details-user-id"
                    onClick={() => copyToClipboard(userIdString)}
                  >
                    {userIdString}{" "}
                    <Clipboard
                      color={theme.palette.text.primary}
                      size={"1rem"}
                      style={{ position: "relative", top: 2 }}
                    />
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={clickableSx}
                    data-anchor="user-details-user-uuid"
                    onClick={() => copyToClipboard(userUUIDString)}
                  >
                    {userUUIDString}{" "}
                    <Clipboard
                      color={theme.palette.text.primary}
                      size={"1rem"}
                      style={{ position: "relative", top: 2 }}
                    />
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ ...leftCellSx, wordBreak: "normal" }}
                  align="right"
                >
                  {"Registered"}
                </TableCell>
                <TableCell
                  sx={rightCellSx}
                  align="left"
                  data-testid="created-at-date"
                >
                  {createdAtDateString}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ ...leftCellSx, wordBreak: "normal" }}
                  align="right"
                >
                  {isEmaillessUser(user) ? "Username" : "Email"}
                </TableCell>
                <TableCell
                  sx={rightCellSx}
                  align="left"
                  data-anchor="user-details-email"
                >
                  <Grid container spacing={1} direction="row">
                    <Grid item>{user?.email ?? "N/A"}</Grid>
                    <Grid item>
                      <Tooltip
                        title={
                          isEmaillessUser(user)
                            ? "Update email or username"
                            : "Update email"
                        }
                      >
                        <IconButton
                          color="primary"
                          data-testid="identifier-update-button"
                          aria-label={
                            isEmaillessUser(user)
                              ? "Update email or username"
                              : "Update email"
                          }
                          style={{
                            position: "relative",
                            bottom: 2,
                            left: 5,
                            padding: 1,
                          }}
                          onClick={() => {
                            setUpdateEmailModalOpen(true);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <Chip
                        variant="outlined"
                        disabled={user?.flags?.isConfirmed}
                        label={
                          user?.flags?.isConfirmed ? "Confirmed" : "Unconfirmed"
                        }
                        color={
                          user?.flags?.isConfirmed ? "default" : "secondary"
                        }
                        style={{
                          color: user?.flags?.isConfirmed
                            ? theme.palette.primary.main
                            : theme.palette.secondary.main,
                          opacity: user?.flags?.isConfirmed ? 0.5 : 1,
                        }}
                        size="small"
                        icon={
                          <Autorenew
                            className={shouldSpin}
                            style={{
                              color: user?.flags?.isConfirmed
                                ? theme.palette.primary.main
                                : theme.palette.secondary.main,
                            }}
                          />
                        }
                        onClick={() => setConfirmUserModalOpen(true)}
                      />
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ ...leftCellSx, wordBreak: "normal" }}
                  align="right"
                >
                  {"Account lockout"}
                </TableCell>
                <TableCell sx={rightCellSx} align="left">
                  <Auth0LoginStatusButton user={user} />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ ...leftCellSx, wordBreak: "normal" }}
                  align="right"
                >
                  {"External Sync"}
                </TableCell>
                <TableCell sx={rightCellSx} align="left">
                  <Chip
                    variant="outlined"
                    disabled={!canSyncUserData}
                    label="Trigger Sync"
                    color="default"
                    style={{
                      color: theme.palette.primary.main,
                      opacity: !canSyncUserData ? 0.5 : 1,
                    }}
                    size="small"
                    icon={
                      <Autorenew
                        className={shouldSpin}
                        style={{ color: theme.palette.primary.main }}
                      />
                    }
                    onClick={() => setConfirmSyncModalOpen(true)}
                  />
                  <Tooltip title="This will trigger a syncing of basic user information to external services (i.e. Kustomer). It is intended to be not limited to a single external service.">
                    <IconButton size="small" sx={{ paddingLeft: "0.25rem" }}>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ ...leftCellSx, wordBreak: "normal" }}
                  align="right"
                >
                  {"Password reset"}
                </TableCell>
                <TableCell sx={rightCellSx} align="left">
                  {!isEmaillessUser(user) && (
                    <Button
                      color="primary"
                      style={{ padding: 0, marginRight: 16 }}
                      onClick={() => setResetEmailModalOpen(true)}
                      data-testid="password-reset-email-button"
                    >
                      Send Email
                    </Button>
                  )}
                  <Button
                    color="primary"
                    style={{ padding: 0 }}
                    onClick={() => setSetPasswordModalOpen(true)}
                    data-testid="set-user-password-button"
                  >
                    Set Password
                  </Button>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell
                  sx={{ ...leftCellSx, wordBreak: "normal" }}
                  align="right"
                >
                  Identity
                </TableCell>
                <TableCell sx={rightCellSx} align="left">
                  {isLoadingGetUserIdentity ? (
                    <CircularProgress />
                  ) : (
                    <div>
                      <Link
                        to={{
                          pathname: `/identity/${identityID}`,
                          state: {
                            userID: user.id,
                            userRegion: regionCode,
                          },
                        }}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <Button
                          style={{
                            padding: "0",
                          }}
                          color="primary"
                          data-testid="identity-page-btn"
                        >
                          Identity <LaunchIcon fontSize="small" />
                        </Button>
                      </Link>
                      {"  "}
                      <Link
                        to={{
                          pathname: `/identity/${identityID}/history`,
                          state: {
                            userName: `${user.firstName} ${user.lastName}`,
                            userID: user.id,
                            userEmail: user.email,
                            userRegion: regionCode,
                          },
                        }}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <Button
                          style={{
                            padding: "0",
                          }}
                          color="primary"
                          data-testid="identity-history-btn"
                        >
                          Identity History <LaunchIcon fontSize="small" />
                        </Button>
                      </Link>
                    </div>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{ ...leftBottomCellSx, wordBreak: "normal" }}
                  align="right"
                >
                  User Attributes
                </TableCell>
                <TableCell sx={rightBottomCellSx} align="left">
                  <Button
                    style={{
                      padding: "0",
                    }}
                    onClick={() => setUserAttributeModalOpen(true)}
                    color="primary"
                    data-testid="identity-details-attributes-btn"
                  >
                    User Attributes
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <UserAttributesDialog
            open={userAttributeModalOpen}
            setIsOpen={setUserAttributeModalOpen}
            roleId={user.orgId}
            userId={user.id}
            regionCode={regionCode}
          />
          <PasswordResetEmailModal
            open={resetEmailModalOpen}
            setOpen={setResetEmailModalOpen}
            userId={user.id}
            userEmail={user.email}
            regionCode={regionCode}
          />
          <SetUserPasswordDialog
            open={setPasswordModalOpen}
            setOpen={setSetPasswordModalOpen}
            userId={user.id}
            userEmail={user.email}
            regionCode={regionCode}
          />
          <UpdateUserEmailModal
            open={updateEmailModalOpen}
            setOpen={setUpdateEmailModalOpen}
            userId={user.id}
            userEmail={user.email}
            regionCode={regionCode}
            orgsCount={user.orgsCount}
            mode={isEmaillessUser(user) ? "emailless" : "edit"}
          />
          <ConfirmUserModal
            open={confirmUserModalOpen}
            setOpen={setConfirmUserModalOpen}
            userId={user.id}
            regionCode={regionCode}
          />
          <ConfirmUserSyncDialog
            open={confirmSyncModalOpen}
            setOpen={setConfirmSyncModalOpen}
            userId={user.id}
            regionCode={regionCode}
          />
        </Box>
      ) : (
        <TableLoading />
      )}
    </React.Fragment>
  );
};

export const UserDetails = ({
  regionCode,
  isLoadingGetUserIdentity,
  identityID,
}: UserDetailsDataProps) => {
  const { user } = useContext(UserContext);
  const userName = `${user?.firstName ?? "N/A"} ${user?.lastName ?? ""}`;
  const [actionMenuAnchor, setActionMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const paperSx = {
    padding: 2,
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  };
  const unsetUserSettings = "Setting not set";
  const userDetails = [
    {
      key: "mobilePhone",
      title: "Mobile Phone",
      value: user.settings?.mobilePhone || unsetUserSettings,
    },
    {
      key: "language",
      title: "Language",
      value:
        (user.settings && languageDisplayMapping[user.settings.language]) ||
        unsetUserSettings,
    },
    {
      key: "timezone",
      title: "Timezone",
      value: user?.settings?.timezone
        ? `${user.settings.timezone} (GMT${
            getTimezone(user.settings.timezone)?.utcOffsetStr ?? "-N/A"
          })`
        : unsetUserSettings,
      testId: "user-timezone-setting",
    },
    {
      key: "dateFormat",
      title: "Date Format",
      value: user.settings?.dateFormat || unsetUserSettings,
    },
    {
      key: "timeFormat",
      title: "Time Format",
      value: user.settings?.timeFormat || unsetUserSettings,
    },
    {
      key: "speedMetric",
      title: "Speed Metric",
      value: getSpeedMetrics(user.settings?.speedMetric) || unsetUserSettings,
    },
    {
      key: "temperatureMetric",
      title: "Temperature Metric",
      value:
        getTemperatureMetrics(user.settings?.temperatureMetric) ||
        unsetUserSettings,
    },
  ];
  return (
    <Grid container spacing={3} padding={5} direction="row">
      <Grid item xs={6}>
        <Paper sx={paperSx}>
          <Grid
            container
            spacing={3}
            style={{ padding: 20 }}
            direction="row"
            alignItems="center"
          >
            <Grid item xs={11}>
              <Box display="flex" justifyContent="center" alignItems="center">
                <DisplayHeading style={{ paddingRight: 3 }}>
                  {userName}
                </DisplayHeading>
                <UserOrgsCountChip
                  orgsCount={user?.orgsCount ?? 0}
                  identityID={identityID}
                  showUsersWithOneOrg={true}
                />
              </Box>
            </Grid>
            <Grid item xs={1}>
              <IconButton
                onClick={(e) => setActionMenuAnchor(e.currentTarget)}
                data-testid="user-details-actions-button"
              >
                <MoreVert />
              </IconButton>
              <Menu
                open={Boolean(actionMenuAnchor)}
                anchorEl={actionMenuAnchor}
                onClose={() => setActionMenuAnchor(null)}
              >
                <MenuItem
                  component={Link}
                  to={{
                    pathname: "/sudo",
                    state: {
                      regionCode: regionCode,
                      userId: user?.id,
                      userName: userName,
                      userEmail: user?.email,
                    },
                  }}
                  sx={{ color: "secondary.main" }}
                  data-testid="sudo-menu-item"
                  data-anchor="sudo-menu-item"
                >
                  SUDO
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
          <Grid container spacing={3} direction="row">
            <Grid
              item
              xs={12}
              sm={12}
              style={{ minWidth: 250, wordBreak: "break-all" }}
            >
              <BasicTable
                user={user}
                regionCode={regionCode}
                isLoadingGetUserIdentity={isLoadingGetUserIdentity}
                identityID={identityID}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={6}>
        <Paper sx={paperSx}>
          <Grid container spacing={3} style={{ padding: 20 }} direction="row">
            <Grid item xs={12}>
              <DisplayHeading>Settings</DisplayHeading>
            </Grid>
          </Grid>
          <Grid container spacing={3} direction="row">
            <Grid
              item
              xs={12}
              sm={12}
              style={{ minWidth: 250, wordBreak: "break-all" }}
            >
              <SettingsTable
                details={userDetails}
                orgId={user.orgId}
                useOrgDatetimeFormat={user.flags?.useOrgDatetimeFormat || false}
                useOrgMetrics={user.flags?.useOrgMetrics || false}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default UserDetails;
